import { forwardRef } from 'react';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type {
  FeedbackButtonProps,
  FeedbackType,
} from '../../../types/feedback';
import {
  FEEDBACK_CLASSES,
  FEEDBACK_TEXT,
  FEEDBACK_RESULT_VALUES,
} from '../../../types/feedback';

/**
 * Get the appropriate icon component for feedback type
 */
const getFeedbackIcon = (type: FeedbackType) => {
  return type === FEEDBACK_RESULT_VALUES.SOLVED ? ThumbsUp : ThumbsDown;
};

/**
 * Get Tailwind classes for feedback button styling
 */
const getFeedbackButtonClasses = (
  type: FeedbackType,
  isSelected: boolean,
  isDisabled: boolean,
  isLoading: boolean,
) => {
  // Base classes for pill-shaped design
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'px-4',
    'py-2',
    'rounded-full',
    'border-2',
    'transition-all',
    'duration-200',
    'text-base',
    'font-medium',
    'whitespace-nowrap',
    'min-h-[36px]',
    'hover:text-gray-500',
  ];

  // State-based classes
  if (isLoading) {
    baseClasses.push('cursor-not-allowed');
  } else if (isDisabled) {
    baseClasses.push('cursor-not-allowed');
  } else {
    baseClasses.push('cursor-pointer');
  }

  // Color classes based on selection state using semantic Tailwind colors
  if (isSelected) {
    if (type === FEEDBACK_RESULT_VALUES.SOLVED) {
      baseClasses.push(
        FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.SOLVED].border,
        FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.SOLVED].text,
        FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.SOLVED].background,
      );
    } else {
      baseClasses.push(
        FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.NOT_HELPFUL].border,
        FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.NOT_HELPFUL].text,
        FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.NOT_HELPFUL].background,
      );
    }
  } else {
    // Default styling with hover border for each type
    baseClasses.push(
      FEEDBACK_CLASSES.default.border,
      FEEDBACK_CLASSES.default.text,
      FEEDBACK_CLASSES.default.background,
    );

    // Add hover styles based on button type (only when not disabled/loading)
    if (!isDisabled && !isLoading) {
      if (type === FEEDBACK_RESULT_VALUES.SOLVED) {
        baseClasses.push(
          FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.SOLVED].hoverBorder,
        );
      } else {
        baseClasses.push(
          FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.NOT_HELPFUL].hoverBorder,
        );
      }
    }
  }

  return baseClasses.join(' ');
};

/**
 * Get icon classes for feedback button icons
 */
const getFeedbackIconClasses = (type: FeedbackType, isSelected: boolean) => {
  const baseClasses = ['size-4'];

  if (isSelected) {
    if (type === FEEDBACK_RESULT_VALUES.SOLVED) {
      baseClasses.push(FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.SOLVED].icon);
    } else {
      baseClasses.push(
        FEEDBACK_CLASSES[FEEDBACK_RESULT_VALUES.NOT_HELPFUL].icon,
      );
    }
  } else {
    baseClasses.push(FEEDBACK_CLASSES.default.icon);
  }

  return baseClasses.join(' ');
};

export const FeedbackButton = forwardRef<
  HTMLButtonElement,
  FeedbackButtonProps
>(
  (
    {
      type,
      children,
      isSelected,
      isDisabled,
      isLoading,
      onClick,
      className,
      ...props
    },
    ref,
  ) => {
    const Icon = getFeedbackIcon(type);
    const buttonText = children || FEEDBACK_TEXT[type];

    // Get Tailwind classes for styling
    const buttonClasses = getFeedbackButtonClasses(
      type,
      isSelected,
      isDisabled,
      isLoading,
    );
    const iconClasses = getFeedbackIconClasses(type, isSelected);

    // ARIA attributes for accessibility
    const ariaLabel = `${buttonText} feedback`;
    const ariaPressed = isSelected;

    return (
      <Button
        ref={ref}
        type='button'
        variant='ghost'
        size='default'
        disabled={isDisabled || isLoading}
        onClick={onClick}
        className={cn(buttonClasses, className)}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        role='button'
        {...props}
      >
        {isLoading ? (
          <Loader2
            className={cn(iconClasses, 'animate-spin')}
            aria-hidden='true'
          />
        ) : (
          <Icon className={iconClasses} aria-hidden='true' />
        )}

        <span className='select-none'>{buttonText}</span>

        {/* Screen reader only text for additional context */}
        <span className='sr-only'>
          {isSelected ? `Selected: ${buttonText}` : `Select ${buttonText}`}
          {isDisabled && ' (disabled)'}
          {isLoading && ' (loading)'}
        </span>
      </Button>
    );
  },
);

FeedbackButton.displayName = 'FeedbackButton';
